/**
 * The agreed price, line by line, the way the client page's Financial Details shows it:
 * package, each add-on, travel, late-booking fee, plan processing fee, promo discount.
 *
 * Same sources and same rules as ClientDetail's Financial Details, so the two screens agree.
 * Post-booking purchases (amendments, portal add-ons) are NOT here: they are their own rows on the
 * timeline, taken from booking_amendments, so they are never counted twice.
 */
import { getInitialBookingAddOns, isPostBookingAddOn } from '../../utils/bookingAddOns.js'
import { getBookingProcessingFeeCents } from '../booking/financials.js'

const cents = v => Math.round(Number(v || 0) * 100)

/**
 * @param {object} booking  a bookings row with personalization_data (and optionally add_ons, travel_fee)
 * @returns {{ lines: Array<{ key: string, title: string, detail?: string, cents: number }>, totalCents: number }}
 */
export function agreedLines(booking) {
  const pd = booking?.personalization_data || {}
  const ps = pd.pricing_summary || {}
  const lines = []

  const processingFeeCents = getBookingProcessingFeeCents(booking)
  const rawPackageTotalCents = Number(booking?.package_total_cents || 0)
  const packageCents = (Number(ps.package_price_cents) || 0) ||
    cents(pd.package?.packagePrice) ||
    (rawPackageTotalCents > 0 ? rawPackageTotalCents - processingFeeCents : 0) ||
    cents(booking?.total_amount)
  // hours and, when the booking carries one, the photo count (custom packages: 8 hours, 410 photos) — Billing and
  // Editing read this line to know what was bought (Trera, 21 Sep 2026)
  const photoCount = Number(pd.package?.photos || 0)
  const packageDetail = [pd.package?.hoursBooked ? `${pd.package.hoursBooked} hours` : null, photoCount > 0 ? `${photoCount} photos` : null].filter(Boolean).join(' · ') || undefined
  lines.push({ key: 'package', title: pd.package?.packageTitle || 'Package', detail: packageDetail, cents: packageCents })

  const directAddOns = Array.isArray(booking?.add_ons) ? booking.add_ons : []
  for (const a of getInitialBookingAddOns(pd, directAddOns).filter(a => !isPostBookingAddOn(a))) {
    const qty = Number(a.qty || 1) || 1
    const each = a.isFree ? 0 : cents(a.price)
    lines.push({
      key: `addon:${a.code || a.id}`,
      title: `Add-on · ${a.title || a.name || a.code || a.id}`,
      detail: a.isFree ? `free${a.promoCode ? ` with ${a.promoCode}` : ''}` : qty > 1 ? `${qty} × ${(each / 100).toFixed(2)}` : undefined,
      cents: each * qty
    })
  }

  const travelCents = cents(booking?.travel_fee) || cents(pd.travelFee) || (Number(ps.travel_fee_cents) || 0)
  if (travelCents > 0) lines.push({ key: 'travel', title: 'Travel fee', cents: travelCents })

  const lateBookingCents = cents(booking?.late_booking_fee) || (Number(ps.late_fee_cents) || 0)
  if (lateBookingCents > 0) lines.push({ key: 'late_booking', title: 'Late booking fee', detail: 'booked close to the event', cents: lateBookingCents })

  // Converted from a cancelled booking (29 Sep 2026): what was kept from it, its own line
  const keptCents = Number(ps.kept_from_cancelled_cents || 0)
  if (keptCents > 0) lines.push({ key: 'kept_from_cancelled', title: `Kept from the cancelled ${pd.kept_from_cancelled?.from || 'booking'}`, detail: 'converted to this package', cents: keptCents })

  if (processingFeeCents > 0) lines.push({ key: 'processing', title: 'Processing fee', detail: booking?.payment_plan === 'monthly99' ? 'monthly plan · the 3 × $99 installments' : 'monthly plan', cents: processingFeeCents })

  const promo = pd.promotion
  if (promo?.discount_type === 'money_off' && Number(promo.discount_value) > 0) {
    lines.push({ key: 'promo', title: `Promo ${promo.code || ''}`.trim(), detail: 'money off', cents: -cents(promo.discount_value) })
  } else if (promo?.discount_type === 'percentage_off' && Number(promo.discount_value) > 0) {
    lines.push({ key: 'promo', title: `Promo ${promo.code || ''}`.trim(), detail: `${promo.discount_value}% off the package`, cents: -Math.round(packageCents * Number(promo.discount_value) / 100) })
  }

  const totalCents = lines.reduce((s, l) => s + l.cents, 0)
  return { lines, totalCents }
}
